import { supabase } from "@/integrations/supabase/client";
import type { QueryData } from "@supabase/supabase-js";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export type UpsertOpcs4RuleData = {
  name: string;
  rule_text: string;
  is_active: boolean;
  codeIds: string[];
};

const getOpcs4CodeRuleQuery = () => {
  return supabase
    .from("opcs_4_code_rules")
    .select(
      `
      *,
      opcs4CodeRuleAssignments:opcs_4_code_rule_assignments!inner(
        *,
        opcs4Code:opcs4_codes!inner(
          id,
          code,
          description
        )
      )
    `,
    )
    .order("created_at", { ascending: false });
};

export type Opcs4CodeRule = QueryData<ReturnType<typeof getOpcs4CodeRuleQuery>>[number];

export const useOpcs4CodeRules = ({
  searchQuery,
  codeSearchQuery,
}: {
  searchQuery?: string;
  codeSearchQuery?: string;
}) => {
  const [rules, setRules] = useState<Opcs4CodeRule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Fetch initial data and on filter change
  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setError(null);

    const fetchRules = async () => {
      try {
        let query = getOpcs4CodeRuleQuery().order("name");

        if (searchQuery) {
          query = query.or(`name.ilike.%${searchQuery}%,rule_text.ilike.%${searchQuery}%`);
        }

        if (codeSearchQuery) {
          // First, find codes that match the search
          const { data: matchingCodes, error: codeError } = await supabase
            .from("opcs4_codes")
            .select("id")
            .ilike("code", `%${codeSearchQuery}%`);

          if (codeError) {
            throw codeError;
          }

          if (matchingCodes && matchingCodes.length > 0) {
            const codeIds = matchingCodes.map((code) => code.id);

            // Then filter rules by those code IDs
            query = query.in("opcs4CodeRuleAssignments.code_id", codeIds);
          } else {
            // No matching codes found, return empty result
            setRules([]);
            setIsLoading(false);
            return;
          }
        }

        const { data, error } = await query;

        if (!isMounted) return;
        if (error) {
          setError(error);
          setIsLoading(false);
          toast.error("Failed to load OPCS-4 code rules. Please refresh the page.");
        } else {
          setRules(data);
          setIsLoading(false);
        }
      } catch (error) {
        if (!isMounted) return;
        setError(error as Error);
        setIsLoading(false);
        toast.error("Failed to load OPCS-4 code rules. Please refresh the page.");
      }
    };

    fetchRules();

    return () => {
      isMounted = false;
    };
  }, [searchQuery, codeSearchQuery]);

  // Subscribe to realtime changes
  useEffect(() => {
    const channel = supabase
      .channel("opcs_4_code_rules-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "opcs_4_code_rules",
        },
        async (payload) => {
          const { eventType, new: newRow, old: oldRow } = payload;
          if (eventType === "DELETE") {
            setRules((prev) => {
              return prev.filter((rule) => rule.id !== oldRow.id);
            });
          } else {
            const { data: newEnrichedRule } = await getOpcs4CodeRuleQuery().eq("id", newRow.id).single();

            if (newEnrichedRule) {
              if (eventType === "UPDATE") {
                setRules((prev) => {
                  return prev.map((rule) => (rule.id === newRow.id ? { ...rule, ...newEnrichedRule } : rule));
                });
              } else {
                setRules((prev) => [newEnrichedRule, ...prev]);
              }
            }
          }
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const toggleRuleActive = async (ruleId: string, currentIsActive: boolean) => {
    try {
      const { error } = await supabase
        .from("opcs_4_code_rules")
        .update({ is_active: !currentIsActive })
        .eq("id", ruleId);

      if (error) {
        toast.error("Failed to update rule status. Please try again.");
        throw error;
      }

      // Optimistically update the local state
      setRules((prev) => prev.map((rule) => (rule.id === ruleId ? { ...rule, is_active: !currentIsActive } : rule)));

      toast.success(`Rule ${!currentIsActive ? "activated" : "deactivated"} successfully.`);
    } catch (error) {
      console.error("Error toggling rule active status:", error);
    }
  };

  const _queryClient = useQueryClient();

  const upsertRuleMutation = useMutation({
    mutationFn: async ({ ruleData, ruleId }: { ruleData: UpsertOpcs4RuleData; ruleId?: string }) => {
      if (!ruleData.name.trim() || !ruleData.rule_text.trim()) {
        throw new Error("Name and rule text are required");
      }

      if (ruleData.codeIds.length === 0) {
        throw new Error("At least one OPCS-4 code must be selected");
      }

      const isEditing = !!ruleId;

      if (isEditing) {
        // Update existing rule
        const { data: updatedRule, error: updateError } = await supabase
          .from("opcs_4_code_rules")
          .update({
            name: ruleData.name.trim(),
            rule_text: ruleData.rule_text.trim(),
            is_active: ruleData.is_active,
            updated_at: new Date().toISOString(),
          })
          .eq("id", ruleId)
          .select()
          .single();

        if (updateError) throw updateError;

        // Delete existing assignments and create new ones
        await supabase.from("opcs_4_code_rule_assignments").delete().eq("rule_id", ruleId);

        if (ruleData.codeIds.length > 0) {
          const assignments = ruleData.codeIds.map((codeId) => ({
            rule_id: ruleId,
            code_id: codeId,
          }));

          const { error: assignmentError } = await supabase.from("opcs_4_code_rule_assignments").insert(assignments);

          if (assignmentError) throw assignmentError;
        }

        // Fetch the updated rule with assignments
        const { data: enrichedRule } = await getOpcs4CodeRuleQuery().eq("id", ruleId).single();

        if (enrichedRule) {
          // Update local state
          setRules((prev) => prev.map((rule) => (rule.id === ruleId ? enrichedRule : rule)));
        }

        toast.success("Rule updated successfully");
        return enrichedRule;
      } else {
        // Create new rule
        const { data: newRule, error: createError } = await supabase
          .from("opcs_4_code_rules")
          .insert({
            name: ruleData.name.trim(),
            rule_text: ruleData.rule_text.trim(),
            is_active: ruleData.is_active,
          })
          .select()
          .single();

        if (createError) throw createError;

        // Create assignments
        if (ruleData.codeIds.length > 0) {
          const assignments = ruleData.codeIds.map((codeId) => ({
            rule_id: newRule.id,
            code_id: codeId,
          }));

          const { error: assignmentError } = await supabase.from("opcs_4_code_rule_assignments").insert(assignments);

          if (assignmentError) throw assignmentError;
        }

        // Fetch the new rule with assignments
        const { data: enrichedRule } = await getOpcs4CodeRuleQuery().eq("id", newRule.id).single();

        if (enrichedRule) {
          // Add to local state
          setRules((prev) => [enrichedRule, ...prev]);
        }

        toast.success("Rule created successfully");
        return enrichedRule;
      }
    },
    onError: (error: Error) => {
      console.error("Error upserting rule:", error);
      toast.error(error.message || "Failed to upsert rule. Please try again.");
    },
  });

  const deleteRuleMutation = useMutation({
    mutationFn: async (ruleId: string) => {
      // Delete assignments first
      await supabase.from("opcs_4_code_rule_assignments").delete().eq("rule_id", ruleId);

      // Delete the rule
      const { error } = await supabase.from("opcs_4_code_rules").delete().eq("id", ruleId);

      if (error) throw error;

      // Update local state
      setRules((prev) => prev.filter((rule) => rule.id !== ruleId));

      toast.success("Rule deleted successfully");
    },
    onError: (error: Error) => {
      console.error("Error deleting rule:", error);
      toast.error("Failed to delete rule. Please try again.");
    },
  });

  return {
    data: rules,
    isLoading,
    error,
    toggleRuleActive,
    upsertRuleMutation,
    deleteRuleMutation,
  };
};
